import { memo, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { SettingBlock } from '~/components/dialogs/settings/SettingBlock';
import { Button } from '~/components/ui/Button';
import { Switch } from '~/components/ui/Switch';
import { UpgradeLink } from '~/components/ui/UpgradeLink';
import { settingsService } from '~/lib/services/settings-service';
import { menuStore } from '~/lib/stores/menu';
import { featurePreviewsStore } from '~/lib/stores/settings';
import { HELP_CENTER_URL } from '~/lib/urls';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

export const FeaturePreviews = memo<{ canAccess: boolean }>(({ canAccess }) => {
  const [features, setFeatures] = useState(() => featurePreviewsStore.get());
  const [saving, setSaving] = useState(false);

  const { unsavedChanges, showWarning } = useMemo(() => {
    const storedFeatures = featurePreviewsStore.get();

    const showWarning = Object.keys(features).some((key) => {
      const typedKey = key as keyof typeof features;

      return features[typedKey] && !storedFeatures[typedKey];
    });

    return {
      unsavedChanges: JSON.stringify(features) !== JSON.stringify(storedFeatures),
      showWarning,
    };
  }, [features]);

  const handleSave = async () => {
    try {
      await withSpinner({
        action: async () => {
          await settingsService.setMultiple('featurePreviews', {
            reasoning: features.reasoning,
          });

          setFeatures(featurePreviewsStore.get());
          setSaving(false);
        },
        onStateUpdate: (isSpinning, prevValue) => {
          if (isSpinning && !prevValue) {
            setSaving(true);
          }
        },
      });
    } catch (error) {
      setSaving(false);

      logger.error(error);

      toast.error('Failed to update feature previews');
    }
  };

  return (
    <SectionContainer title="Experimental features" className="pb-6">
      {!canAccess && (
        <div className="text-sm text-bolt-elements-textPrimary bg-alpha-orange-10 p-2 border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500 text-left">
          Experimental features are only available on paid plans.{' '}
          <UpgradeLink
            className="inline-block"
            text="Upgrade"
            onClick={() => {
              menuStore.closeDialog();
            }}
          />{' '}
          to a paid account to get access.
        </div>
      )}
      <SettingBlock
        id="dynamic-reasoning"
        title="Dynamic Reasoning"
        description="Improves results on complex or logic-heavy problems by allowing deeper reasoning. May result in longer
            response times or higher token use."
        className="w-4/5"
        action={
          <Switch
            id="dynamic-reasoning"
            checked={features.reasoning}
            disabled={!canAccess && !features.reasoning}
            onCheckedChange={(value) => {
              if (canAccess) {
                setFeatures({ ...features, reasoning: value });
              }
            }}
          />
        }
      />
      {unsavedChanges && (
        <>
          {showWarning && (
            <div className="bg-alpha-orange-10 border-l border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500 text-sm">
              Experimental features are not validated for production usage and may introduce bugs. If you enable
              experiments, please be prepared to report your findings to the engineering team in the{' '}
              <a
                href={HELP_CENTER_URL}
                className="text-bolt-elements-messages-linkColor hover:underline"
                target="_blank"
              >
                Help Center
              </a>
              .
            </div>
          )}
          <Button className="self-end gap-1.5" size="md" onClick={handleSave} disabled={saving} loading={saving}>
            Save
          </Button>
        </>
      )}
    </SectionContainer>
  );
});
